import React, { FC } from 'react'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import {
    UseFormRegister,
    Control,
    useWatch,
    FieldErrors,
    UseFormUnregister
} from 'react-hook-form'
import type { GiftCardProductInputArgs } from './GiftCardProduct'
import type { GiftCardProduct } from '@pylot-data/pylotschema'
import { OtherAmountEnum } from './AmountDropdown'
import s from '@pagestyles/Giftcard.module.scss'
import { SET_MESSAGE, SET_TARGET } from './reducer'
import { formatPrice } from '@corratech/pylot-price'
import { useRouter } from 'next/router'
import { PriceRange } from '@pylot-data/pylotschema'

type SelectedAmountAction =
    | { type: 'SET_MESSAGE'; payload: string }
    | { type: 'SET_TARGET'; payload: string }

export type Props = {
    register: UseFormRegister<GiftCardProductInputArgs>
    control: Control<GiftCardProductInputArgs>
    errors: FieldErrors<GiftCardProductInputArgs>
    unregister: UseFormUnregister<GiftCardProductInputArgs>
    product: GiftCardProduct
    dispatch: React.Dispatch<SelectedAmountAction>
    price_range: PriceRange
}

export const OpenAmount: FC<Props> = ({
    register,
    control,
    errors,
    dispatch,
    product,
    price_range
}) => {
    const { t } = useTranslation(['pdp'])
    const {
        allow_open_amount,
        giftcard_amounts,
        open_amount_min,
        open_amount_max
    } = product
    const { locale = 'en' } = useRouter()

    const amount = useWatch({
        control,
        name: 'gift_amounts',
        defaultValue: allow_open_amount ? OtherAmountEnum.Other : ''
    })

    const validateAmount = (value: string | number) => {
        if (isNaN(Number(value)))
            return t('pdp||giftCard|Please enter a valid number')
        if (value == 0)
            return t('pdp||giftCard|Price must be greater than zero')
        if (
            value < Number(open_amount_min) ||
            value > Number(open_amount_max)
        ) {
            return t(
                `pdp||giftCard|Amount must be between {{open_amount_min}} and {{open_amount_max}}`,
                {
                    open_amount_min: formatPrice(
                        price_range.minimum_price.final_price,
                        locale
                    ),
                    open_amount_max: formatPrice(
                        price_range.maximum_price!.final_price,
                        locale
                    )
                }
            )
        }
        return true
    }

    const handleAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
        const inputValue = event.currentTarget.value
        dispatch({
            type: SET_TARGET,
            payload: 'other'
        })
        if (validateAmount(inputValue) === true) {
            dispatch({
                type: SET_MESSAGE,
                payload: formatPrice(
                    {
                        ...price_range.maximum_price!.final_price,
                        value: Number(inputValue)
                    },
                    locale
                )
            })
        } else {
            dispatch({
                type: SET_MESSAGE,
                payload: t(`pdp||giftCard|From {{min}} to {{max}}`, {
                    min: formatPrice(
                        price_range.minimum_price.final_price,
                        locale
                    ),
                    max: formatPrice(
                        price_range.maximum_price!.final_price,
                        locale
                    )
                })
            })
        }
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const numericRegex = /^\d$/
        if (!numericRegex.test(event.key) && event.key !== 'Backspace') {
            event.preventDefault()
        }
    }

    const getOpenAmountErrors = () => {
        if (errors.gift_open_amounts?.message) {
            return t('pdp||giftCard|{{ fieldError }}', {
                fieldError: errors.gift_open_amounts?.message
            })
        }

        if (errors?.gift_open_amounts?.type === 'min') {
            return t(
                'pdp||giftCard|Please enter a value greater than or equal to {{ min }}.',
                {
                    min: formatPrice(
                        price_range.minimum_price.final_price,
                        locale
                    )
                }
            )
        }

        if (errors?.gift_open_amounts?.type === 'max') {
            return t(
                'pdp||giftCard|Please enter a value less than or equal to {{ max }}.',
                {
                    max: formatPrice(
                        price_range.maximum_price!.final_price,
                        locale
                    )
                }
            )
        }

        return ''
    }

    if (
        !allow_open_amount ||
        (amount !== OtherAmountEnum.Other && giftcard_amounts?.length)
    ) {
        return null
    }

    return (
        <div className="open-amount-container">
            <div className="form-group">
                <label className="heading" htmlFor="gift_open_amounts">
                    {t('pdp||giftCard|Select your amount')}
                    <span className="asterisk">*</span>
                </label>
                <input
                    type="text"
                    {...register('gift_open_amounts', {
                        required: 'This is a required field',
                        valueAsNumber: true,
                        validate: validateAmount
                    })}
                    defaultValue={Number(open_amount_min)}
                    className={cn('form-input', {
                        'border-alert-danger': errors.gift_open_amounts
                    })}
                    id="gift_open_amounts"
                    onInput={handleAmountChange}
                    onKeyPress={handleKeyPress}
                />
                {errors.gift_open_amounts && (
                    <span className="error">{getOpenAmountErrors()}</span>
                )}
                {/* todo: work on currency converter */}
                <div className={s['open-amount-note']}>
                    {t('pdp||giftCard|Minimum: {{ min }} Maximum: {{ max }}', {
                        min: formatPrice(
                            price_range.minimum_price.final_price,
                            locale
                        ),
                        max: formatPrice(
                            price_range.maximum_price!.final_price,
                            locale
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
